/**
 * ISO 8601 validation for user-entered timestamps.
 *
 * The platform stores and compares timestamps as offset-aware instants
 * (API_CONTRACT §3). A naive value like `2026-03-01T00:00:00` is ambiguous: the
 * backend reads it as UTC, so a Pacific-midnight entry lands on the wrong DUT
 * day and skews day-based accounting (e.g. the substitution threshold) or fails
 * server-side comparisons against an offset-aware `now`. So a valid instant must
 * carry an explicit offset — `Z` or `±HH:MM` — and round-trip through
 * `Date.parse`. This is a client-side typo guard only; the backend stays
 * authoritative for rules we can't check here (future-dated, non-overlapping…).
 */
import { formatPacific, parseInstantWithOffset, ptWallTimeToUtc } from "@compliance/domain";

const ISO_8601_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/;

/** True when `value` is an offset-aware ISO 8601 instant that `Date.parse` accepts. */
export function isIsoInstant(value: string): boolean {
  const trimmed = value.trim();
  return ISO_8601_WITH_OFFSET.test(trimmed) && !Number.isNaN(Date.parse(trimmed));
}

const LOCAL_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Convert a native `<input type="datetime-local">` value (naive
 * "YYYY-MM-DDTHH:mm[:ss]") — which the operator enters as Pacific wall-clock —
 * into the offset-aware UTC instant the backend expects (see isIsoInstant).
 * DST-aware via the domain's ptWallTimeToUtc, so a PT entry never lands on the
 * wrong DUT day. Returns null when the value isn't a complete datetime-local.
 */
export function pacificLocalToInstant(local: string): string | null {
  const m = LOCAL_DATETIME.exec(local.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const utc = ptWallTimeToUtc(
    Number(y),
    Number(mo),
    Number(d),
    Number(h),
    Number(mi),
    s ? Number(s) : 0,
  );
  return Number.isNaN(utc.getTime()) ? null : utc.toISOString();
}

/**
 * Render a backend ISO instant in Pacific Time (ISO 8601 with the PT offset),
 * so the platform's "(PT)" columns actually show PT rather than the raw,
 * possibly-UTC offset the API sends. Returns null for a missing value (callers
 * supply their own placeholder) and falls back to the raw string if it can't be
 * parsed — never throws in a render path.
 */
export function formatPacificDisplay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return formatPacific(parseInstantWithOffset(iso));
  } catch {
    return iso;
  }
}

/**
 * Render a `[period_start, period_end]` range in Pacific Time. The BE emits these
 * boundaries as UTC instants; rendering them raw leaked UTC into the corrections
 * detail's Period field while the sibling "(PT)" audit timestamps were localized
 * (BIO-215). This mirrors how the operator enters the period — PT wall-clock via
 * `pacificLocalToInstant`. Each bound falls back to an em dash; returns null when
 * both are absent so the caller can drop the row.
 */
export function formatPacificRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start && !end) return null;
  return `${formatPacificDisplay(start) ?? "—"} → ${formatPacificDisplay(end) ?? "—"}`;
}

/**
 * Audience-friendly Pacific timestamp for detail views (BIO-256):
 *   "2025-11-06T00:00:00-08:00"  →  "2025-11-06 00:00:00 PT"
 * Built on `formatPacificDisplay` (which localizes to PT), then drops the ISO
 * `T` separator and the numeric offset in favor of a plain "PT" label (covers
 * both PST and PDT), so callers can also drop the redundant "(PT)" column/label
 * suffix. Falls back to `formatPacificDisplay`'s output if the shape is
 * unexpected; null when the value is missing. Kept SEPARATE from
 * `formatPacificDisplay`, which must stay ISO-with-offset for the logic callers
 * (`correctionPick` / `correctionOverlay`) that parse/compare its output.
 */
export function formatPacificFriendly(iso: string | null | undefined): string | null {
  const display = formatPacificDisplay(iso);
  if (!display) return display;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/.exec(display);
  return m ? `${m[1]} ${m[2]} PT` : display;
}

/**
 * Friendly Pacific range for detail views (BIO-256):
 *   "2025-11-06 00:00:00 PT → 2025-11-07 00:00:00 PT"
 * Each bound falls back to an em dash; null when both are absent.
 */
export function formatPacificFriendlyRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start && !end) return null;
  return `${formatPacificFriendly(start) ?? "—"} → ${formatPacificFriendly(end) ?? "—"}`;
}

const PERIOD_PREFIX_RE = /^(\d{4})-(\d{2})/;
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Derive a "March 2026"-style month-year label from a batch `period_start`.
 * The leading `YYYY-MM` IS the Pacific reporting month — the same convention as
 * `quarters.ts` (`quarterOf`) and `dateRange.isoToGasDay`. We read it straight
 * off the string rather than `new Date()` + a UTC reprojection, which would
 * mislabel a batch whose PT calendar date differs from its UTC date (BIO-144
 * #11). Falls back to the YYYY-MM prefix on an unparseable value rather than
 * throwing in a render path.
 */
export function periodLabelFrom(periodStart: string): string {
  const m = PERIOD_PREFIX_RE.exec(periodStart);
  if (!m) return periodStart.slice(0, 7);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return periodStart.slice(0, 7);
  return `${MONTH_NAMES[month - 1]} ${m[1]}`;
}
